// src/Pages/RescheduleInterview.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { db } from "../firebase";
import { collection, getDocs, query, where, deleteDoc, doc, getDoc } from "firebase/firestore";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import LoadingAnimation from "../components/LoadingAnimation";
import "./RescheduleInterview.css";
import {
  lookupApplication,
  rescheduleSlotReservation,
  validateInterviewDate,
  getMinSelectableDate,
  formatDateToISO,
  formatToAppDateString,
  createSlotReservation,
  releaseReservation,
  buildReservationDocId,
  getOrCreateUserId,
  getOrCreateTabId,
  RESERVATION_TTL_MS,
  sendAdminRescheduleEmail,
  sendCandidateRescheduleEmail,
} from "../utils/slotReservation";

// ─── Inline styles — glassmorphic design matching the VBT career site ─────────
const S = {
  page: {
    minHeight: "100vh",
    background: "#f5f5f5",
    paddingTop: "120px",
    paddingBottom: "70px",
    fontFamily: "'poppins-regular', sans-serif",
  },
  container: {
    maxWidth: "860px",
    margin: "0 auto",
    padding: "0 50px",
  },
  pageTitle: {
    textAlign: "center",
    marginBottom: "36px",
  },
  badge: {
    display: "inline-block",
    background: "#fff3e6",
    border: "1px solid #ffd9b2",
    color: "#ff6b00",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "1px",
    textTransform: "uppercase",
    padding: "5px 14px",
    borderRadius: "25px",
    marginBottom: "14px",
  },
  h1: {
    margin: "0 0 10px",
    fontFamily: "'karantina-bold', sans-serif",
    fontSize: "42px",
    fontWeight: "800",
    color: "#000000",
    lineHeight: 1.2,
  },
  subtitle: {
    margin: 0,
    fontSize: "16px",
    color: "#555555",
    lineHeight: 1.6,
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e5e5e5",
    borderRadius: "25px",
    padding: "36px 40px",
    marginBottom: "24px",
    boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
  },
  stepHeader: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    marginBottom: "28px",
  },
  stepBadge: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    background: "#ff8c00",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: "800",
    color: "#fff",
    flexShrink: 0,
    boxShadow: "none",
  },
  stepTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: "700",
    color: "#222222",
  },
  stepDesc: {
    margin: "3px 0 0",
    fontSize: "12px",
    color: "#777777",
  },
  label: {
    display: "block",
    fontSize: "12px",
    fontWeight: "700",
    color: "#444444",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    marginBottom: "8px",
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    background: "#ffffff",
    border: "1px solid #d5d5d5",
    borderRadius: "10px",
    color: "#222222",
    fontSize: "15px",
    fontFamily: "'poppins-regular', sans-serif",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  inputError: {
    borderColor: "#e53e3e",
  },
  fieldError: {
    fontSize: "11px",
    color: "#e53e3e",
    marginTop: "5px",
  },
  btn: {
    width: "100%",
    padding: "15px",
    background: "#ff8c00",
    border: "none",
    borderRadius: "12px",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    letterSpacing: "0.5px",
    marginTop: "8px",
    transition: "opacity 0.2s, transform 0.1s",
    boxShadow: "none",
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  btnSecondary: {
    background: "#ffffff",
    border: "1px solid #d5d5d5",
    boxShadow: "none",
  },
  divider: {
    height: "1px",
    background: "#e5e5e5",
    margin: "24px 0",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "10px 0",
    borderBottom: "1px solid #eeeeee",
    fontSize: "13px",
  },
  summaryLabel: {
    color: "#777777",
    fontWeight: "600",
    textTransform: "uppercase",
    fontSize: "11px",
    letterSpacing: "0.6px",
    minWidth: "120px",
  },
  summaryValue: {
    color: "#222222",
    fontWeight: "500",
    textAlign: "right",
    flex: 1,
  },
  timerBar: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
    background: "#fff8f0",
    border: "1px solid #ffd9b2",
    borderRadius: "10px",
    marginBottom: "16px",
    fontSize: "13px",
    color: "#b35900",
    fontWeight: "600",
  },
  successCard: {
    textAlign: "center",
    padding: "48px 36px",
  },
  successIcon: {
    fontSize: "52px",
    marginBottom: "16px",
    display: "block",
  },
  successTitle: {
    margin: "0 0 10px",
    fontSize: "22px",
    fontWeight: "800",
    color: "#218838",
  },
  successMsg: {
    fontSize: "14px",
    color: "#555555",
    margin: "0 0 28px",
    lineHeight: 1.6,
  },
  slotGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
    gap: "10px",
  },
  slotBtn: {
    padding: "12px 10px",
    borderRadius: "10px",
    border: "1px solid #d5d5d5",
    background: "#ffffff",
    color: "#444444",
    fontSize: "13px",
    fontWeight: "500",
    cursor: "pointer",
    textAlign: "center",
    transition: "all 0.2s",
  },
  slotBtnSelected: {
    background: "#fff3e6",
    border: "1px solid #ff8c00",
    color: "#c05621",
    fontWeight: "700",
  },
  slotBtnBooked: {
    background: "#f5f5f5",
    color: "#999999",
    opacity: 0.65,
    cursor: "not-allowed",
    textDecoration: "line-through",
  },
};

// ─── CNIC auto-formatter: inserts dashes at positions 5 and 12 ───────────────
const formatCnic = (raw) => {
  const digits = raw.replace(/\D/g, "").slice(0, 13);
  if (digits.length <= 5) return digits;
  if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
};

const stripCnicDashes = (val) => val.replace(/-/g, "");

// ─── Component ────────────────────────────────────────────────────────────────
const RescheduleInterview = () => {
  // ── Step 1: Identity Lookup ──────────────────────────────────────────────
  const [step, setStep] = useState(1); // 1 | 2 | 3 (success)
  const [cnic, setCnic] = useState("");
  const [email, setEmail] = useState("");
  const [cnicErr, setCnicErr] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);

  // ── Found application ────────────────────────────────────────────────────
  const [appDoc, setAppDoc] = useState(null);          // Firestore DocumentSnapshot
  const [appCollection, setAppCollection] = useState(""); // collection name
  const [appData, setAppData] = useState(null);           // .data() snapshot

  // ── Step 2: Rescheduling controls ───────────────────────────────────────
  const [newDate, setNewDate] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [reason, setReason] = useState("");
  const [holidayDates, setHolidayDates] = useState([]);
  const [isCheckingSlots, setIsCheckingSlots] = useState(false);
  const [isReserving, setIsReserving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [myReservation, setMyReservation] = useState(null);  // { docId, expiresAt, slotTime }
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [dateErr, setDateErr] = useState("");
  const [slotErr, setSlotErr] = useState("");
  const [reasonErr, setReasonErr] = useState("");

  const userId = useRef(getOrCreateUserId()).current;
  const tabId = useRef(getOrCreateTabId()).current;
  const countdownRef = useRef(null);

  // ── Min date for the date picker ─────────────────────────────────────────
  const minDateStr = formatDateToISO(getMinSelectableDate(3, holidayDates));

  // ── Load slots from Firestore `slots` collection ──────────────────────────
  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const snap = await getDocs(collection(db, "slots"));
        const list = snap.docs.map((d) => ({ label: d.data().slot, time: d.data().time }));
        setAvailableSlots(list);
      } catch {
        toast.error("Failed to load interview time slots.");
      }
    };
    fetchSlots();
  }, []);

  // ── Load public holidays ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "publicHolidays"));
        if (snap.exists()) setHolidayDates(snap.data().dates || []);
      } catch {
        // Non-fatal — proceed without holiday data
      }
    };
    fetchHolidays();
  }, []);

  // ── Countdown timer ───────────────────────────────────────────────────────
  const startCountdown = useCallback((expiresAt) => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    const tick = () => {
      const remaining = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
        setMyReservation(null);
        setSelectedSlot("");
        toast.warning("Your slot reservation expired. Please select a slot again.");
      }
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
  }, []);

  useEffect(() => {
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  }, []);

  // ── Fetch booked slots for a given date ───────────────────────────────────
  const fetchBookedSlots = useCallback(async (dateISO) => {
    setIsCheckingSlots(true);
    setBookedSlots([]);
    try {
      const formattedDate = formatToAppDateString(dateISO);
      const now = new Date();

      const [jobSnap, oppSnap, resSnap] = await Promise.all([
        getDocs(query(collection(db, "jobApplications"), where("interviewDate", "==", formattedDate))),
        getDocs(query(collection(db, "opportunityApplications"), where("interviewDate", "==", formattedDate))),
        getDocs(query(collection(db, "slotReservations"), where("interviewDate", "==", dateISO))),
      ]);

      const fromJob = jobSnap.docs.map((d) => d.data().interviewSlot).filter(Boolean);
      const fromOpp = oppSnap.docs.map((d) => d.data().interviewSlot).filter(Boolean);

      const tempBooked = [];
      resSnap.docs.forEach((d) => {
        const data = d.data();
        if (data.status === "temporary") {
          if (data.expiresAt.toDate() <= now) {
            deleteDoc(doc(db, "slotReservations", d.id)).catch(() => {});
          } else if (data.tabId !== tabId) {
            tempBooked.push(data.slotTime);
          }
        }
      });

      setBookedSlots([...fromJob, ...fromOpp, ...tempBooked]);
    } catch {
      toast.error("Could not load slot availability. Please try again.");
    } finally {
      setIsCheckingSlots(false);
    }
  }, [tabId]);

  // ── Step 1: Find application ──────────────────────────────────────────────
  const handleLookup = async () => {
    // Validate CNIC
    const rawCnic = stripCnicDashes(cnic);
    let valid = true;
    if (!rawCnic || rawCnic.length !== 13) {
      setCnicErr("Please enter a valid 13-digit CNIC.");
      valid = false;
    } else {
      setCnicErr("");
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailErr("Please enter a valid email address.");
      valid = false;
    } else {
      setEmailErr("");
    }
    if (!valid) return;

    setLookupLoading(true);
    try {
      const result = await lookupApplication(db, rawCnic, email.trim().toLowerCase());
      if (!result) {
        toast.error("No application found matching this CNIC and email. Please check your details.");
        return;
      }
      const data = result.doc.data();
      setAppDoc(result.doc);
      setAppCollection(result.collectionName);
      setAppData(data);
      setStep(2);
      toast.success("Application found! You can now reschedule below.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLookupLoading(false);
    }
  };

  // ── Step 2: Date change → refresh slots ──────────────────────────────────
  const handleDateChange = async (e) => {
    const val = e.target.value;
    setNewDate(val);
    setSelectedSlot("");
    setDateErr("");

    // Release any existing slot reservation
    if (myReservation) {
      releaseReservation(db, myReservation.docId);
      if (countdownRef.current) clearInterval(countdownRef.current);
      setMyReservation(null);
      setSecondsLeft(0);
    }

    if (!val) return;

    const validation = validateInterviewDate(val, 3, holidayDates);
    if (!validation.valid) {
      setDateErr(validation.error);
      return;
    }

    await fetchBookedSlots(val);
  };

  // ── Step 2: Select a slot → create 2-min reservation ─────────────────────
  const handleSlotSelect = async (slotTime) => {
    if (bookedSlots.includes(slotTime) || isReserving) return;
    if (myReservation?.slotTime === slotTime) return;

    setIsReserving(true);
    setSlotErr("");

    // Release previous reservation
    if (myReservation) {
      releaseReservation(db, myReservation.docId);
      if (countdownRef.current) clearInterval(countdownRef.current);
      setMyReservation(null);
      setSecondsLeft(0);
    }

    try {
      const { docId, expiresAt } = await createSlotReservation(
        db, newDate, slotTime, userId, tabId, holidayDates
      );
      setSelectedSlot(slotTime);
      setMyReservation({ docId, expiresAt, slotTime });
      startCountdown(expiresAt);
    } catch (err) {
      if (err.message === "SLOT_TEMPORARILY_RESERVED") {
        toast.warning("That slot was just taken by someone else. Please pick another.");
        await fetchBookedSlots(newDate);
      } else if (err.message === "SLOT_PERMANENTLY_TAKEN") {
        toast.error("That slot is already confirmed. Please pick another.");
        await fetchBookedSlots(newDate);
      } else {
        toast.error("Could not reserve slot. Please try again.");
      }
      setSelectedSlot("");
    } finally {
      setIsReserving(false);
    }
  };

  // ── Step 2: Validate & submit reschedule ──────────────────────────────────
  const handleSubmit = async () => {
    let valid = true;

    const dateValidation = validateInterviewDate(newDate, 3, holidayDates);
    if (!dateValidation.valid) { setDateErr(dateValidation.error); valid = false; }
    else setDateErr("");

    if (!selectedSlot) { setSlotErr("Please select an interview time slot."); valid = false; }
    else setSlotErr("");

    if (!reason.trim() || reason.trim().length < 10) {
      setReasonErr("Please provide a reason (at least 10 characters).");
      valid = false;
    } else setReasonErr("");

    if (!valid) return;
    if (!myReservation) {
      toast.warning("Your slot reservation expired. Please select a slot again.");
      return;
    }

    setIsSubmitting(true);
    try {
      // Derive the old slot doc ID from current interview date + slot
      const oldSlotDocId = appData?.interviewDate && appData?.interviewSlot
        ? buildReservationDocId(
            // Convert DD-MM-YYYY to YYYY-MM-DD for the doc ID builder
            (() => {
              const parts = (appData.interviewDate || "").split("-");
              return parts.length === 3 && parts[0].length === 2
                ? `${parts[2]}-${parts[1]}-${parts[0]}`
                : appData.interviewDate;
            })(),
            appData.interviewSlot
          )
        : null;

      await rescheduleSlotReservation(
        db,
        appDoc.id,
        appCollection,
        oldSlotDocId,
        newDate,
        selectedSlot,
        reason.trim()
      );

      const oldDate = appData?.interviewDate || "Not set";
      const oldSlot = appData?.interviewSlot || appData?.interviewTime || "Not set";
      const emailResults = await Promise.allSettled([
        sendAdminRescheduleEmail({
          application: appData,
          oldDate,
          oldSlot,
          newDate: formatToAppDateString(newDate),
          newSlot: selectedSlot,
          reason: reason.trim(),
        }),
        sendCandidateRescheduleEmail({
          application: appData,
          newDate: formatToAppDateString(newDate),
          newSlot: selectedSlot,
        }),
      ]);
      if (emailResults.some(({ status: resultStatus }) => resultStatus === "rejected")) {
        console.error("[Reschedule] One or more reschedule emails failed:", emailResults);
        toast.warning("Schedule updated, but one or more email notifications could not be sent.");
      }

      // Clean up the temporary reservation we created during slot selection
      if (myReservation) {
        releaseReservation(db, myReservation.docId);
        if (countdownRef.current) clearInterval(countdownRef.current);
      }

      setStep(3);
      toast.success("Interview successfully rescheduled!");
    } catch (err) {
      console.error("[Reschedule] Error:", err);
      toast.error(err.message || "Reschedule failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Reset to step 1 ───────────────────────────────────────────────────────
  const handleReset = () => {
    setCnic(""); setEmail(""); setCnicErr(""); setEmailErr("");
    setNewDate(""); setSelectedSlot(""); setReason("");
    setDateErr(""); setSlotErr(""); setReasonErr("");
    setAppDoc(null); setAppCollection(""); setAppData(null);
    setMyReservation(null); setSecondsLeft(0);
    if (countdownRef.current) clearInterval(countdownRef.current);
    setStep(1);
  };

  // ── Filtered slot list ────────────────────────────────────────────────────
  const filteredSlots = availableSlots.filter(
    (s) => !bookedSlots.includes(s.time)
  );

  const formatMm = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>Reschedule Interview | VisionBird Technologies</title>
        <meta name="description" content="Reschedule your VisionBird Technologies interview appointment online. Enter your CNIC and email to look up your application and select a new date and time slot." />
      </Helmet>

      <Navbar />

      <div className="reschedule-page" style={S.page}>
        <div className="reschedule-container" style={S.container}>

          {/* ── Page Header ─────────────────────────────────────────────── */}
          <div style={S.pageTitle}>
            <div style={S.badge}>Career Portal</div>
            <h1 className="reschedule-title" style={S.h1}>Reschedule Your Interview</h1>
            <p style={S.subtitle}>
              Enter your CNIC and email to locate your application, then select a new date and slot.
            </p>
          </div>

          {/* ════════════════════════════════════════════════════════════
              STEP 1 — Identity Lookup
          ════════════════════════════════════════════════════════════ */}
          {step === 1 && (
            <div className="reschedule-card" style={S.card}>
              <div style={S.stepHeader}>
                <div style={S.stepBadge}>1</div>
                <div>
                  <p style={S.stepTitle}>Verify Your Identity</p>
                  <p style={S.stepDesc}>We'll look up your application using your CNIC and email.</p>
                </div>
              </div>

              {/* CNIC */}
              <div style={{ marginBottom: "20px" }}>
                <label style={S.label}>
                  <i className="fas fa-id-card" style={{ marginRight: "6px", color: "#FF8C00" }}></i>
                  CNIC Number
                </label>
                <input
                  style={{ ...S.input, ...(cnicErr ? S.inputError : {}) }}
                  placeholder="00000-0000000-0"
                  value={cnic}
                  maxLength={15}
                  onChange={(e) => {
                    setCnicErr("");
                    setCnic(formatCnic(e.target.value));
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleLookup(); }}
                />
                {cnicErr && <p style={S.fieldError}>{cnicErr}</p>}
              </div>

              {/* Email */}
              <div style={{ marginBottom: "28px" }}>
                <label style={S.label}>
                  <i className="fas fa-envelope" style={{ marginRight: "6px", color: "#FF8C00" }}></i>
                  Email Address
                </label>
                <input
                  style={{ ...S.input, ...(emailErr ? S.inputError : {}) }}
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => { setEmailErr(""); setEmail(e.target.value); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleLookup(); }}
                />
                {emailErr && <p style={S.fieldError}>{emailErr}</p>}
              </div>

              <button
                style={{ ...S.btn, ...(lookupLoading ? S.btnDisabled : {}) }}
                onClick={handleLookup}
                disabled={lookupLoading}
              >
                {lookupLoading
                  ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>Looking up…</>
                  : <><i className="fas fa-search" style={{ marginRight: "8px" }}></i>Find My Application</>
                }
              </button>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════
              STEP 2 — Rescheduling Dashboard
          ════════════════════════════════════════════════════════════ */}
          {step === 2 && appData && (
            <>
              {/* ── Application Summary Card ─────────────────────────── */}
              <div className="reschedule-card" style={S.card}>
                <div style={S.stepHeader}>
                  <div style={{ ...S.stepBadge, background: "#e8f5e9", border: "1px solid #a5d6a7", color: "#218838", boxShadow: "none", fontSize: "18px" }}>
                    <i className="fas fa-check"></i>
                  </div>
                  <div>
                    <p style={S.stepTitle}>Application Found</p>
                    <p style={S.stepDesc}>Review your current appointment below.</p>
                  </div>
                </div>

                {[
                  ["Applicant", appData.name || "—"],
                  ["Email", appData.email || "—"],
                  ["Position / Field", appData.selectedField || appData.jobTitle || appData.position || "—"],
                  ["Current Interview Date", appData.interviewDate || "Not set"],
                  ["Current Time Slot", appData.interviewSlot || appData.interviewTime || "Not set"],
                  ["Application Status", appData.status || "—"],
                  ["Application Type", appCollection === "opportunityApplications" ? "Opportunity / Reservation" : "Job Application"],
                ].map(([label, value]) => (
                  <div className="reschedule-summary-row" key={label} style={S.summaryRow}>
                    <span style={S.summaryLabel}>{label}</span>
                    <span style={{ ...S.summaryValue, color: label === "Application Status" ? "#ff8c00" : "#222222" }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              {/* ── Rescheduling Form Card ────────────────────────────── */}
              <div className="reschedule-card" style={S.card}>
                <div style={S.stepHeader}>
                  <div style={S.stepBadge}>2</div>
                  <div>
                    <p style={S.stepTitle}>Select New Date &amp; Time</p>
                    <p style={S.stepDesc}>Minimum 3 working days ahead. Weekends and public holidays are blocked.</p>
                  </div>
                </div>

                {/* New Date */}
                <div style={{ marginBottom: "24px" }}>
                  <label style={S.label}>
                    <i className="fas fa-calendar-alt" style={{ marginRight: "6px", color: "#FF8C00" }}></i>
                    New Interview Date
                  </label>
                  <input
                    type="date"
                    style={{ ...S.input, colorScheme: "light", ...(dateErr ? S.inputError : {}) }}
                    value={newDate}
                    min={minDateStr}
                    onChange={handleDateChange}
                  />
                  {dateErr && <p style={S.fieldError}><i className="fas fa-exclamation-circle" style={{ marginRight: "4px" }}></i>{dateErr}</p>}
                </div>

                {/* Slot Selection */}
                {newDate && !dateErr && (
                  <div style={{ marginBottom: "24px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                      <label style={{ ...S.label, marginBottom: 0 }}>
                        <i className="fas fa-clock" style={{ marginRight: "6px", color: "#ff8c00" }}></i>
                        Select Time Slot
                      </label>
                      <button
                        style={{ background: "none", border: "none", color: "#ff8c00", fontSize: "12px", cursor: "pointer", fontWeight: "600" }}
                        onClick={() => fetchBookedSlots(newDate)}
                        disabled={isCheckingSlots}
                      >
                        <i className={`fas fa-sync-alt ${isCheckingSlots ? "fa-spin" : ""}`} style={{ marginRight: "5px" }}></i>
                        {isCheckingSlots ? "Refreshing…" : "Refresh"}
                      </button>
                    </div>

                    {/* Timer bar */}
                    {myReservation && secondsLeft > 0 && (
                      <div style={S.timerBar}>
                        <i className="fas fa-lock"></i>
                        <span>Slot reserved — expires in <strong>{formatMm(secondsLeft)}</strong></span>
                      </div>
                    )}

                    {slotErr && <p style={{ ...S.fieldError, marginBottom: "10px" }}>{slotErr}</p>}

                    {isCheckingSlots ? (
                      <LoadingAnimation text="Checking availability..." />
                    ) : availableSlots.length === 0 ? (
                      <p style={{ fontSize: "13px", color: "#777777", textAlign: "center", padding: "16px 0" }}>
                        No time slots configured. Please contact us directly.
                      </p>
                    ) : (
                      <div className="reschedule-slot-grid" style={S.slotGrid}>
                        {availableSlots
                          .sort((a, b) => parseInt(a.label?.replace("Slot ", "") || 0) - parseInt(b.label?.replace("Slot ", "") || 0))
                          .map((slot) => {
                            const isBooked = bookedSlots.includes(slot.time);
                            const isSelected = selectedSlot === slot.time;
                            return (
                              <button
                                key={slot.time}
                                disabled={isBooked || isReserving}
                                onClick={() => handleSlotSelect(slot.time)}
                                style={{
                                  ...S.slotBtn,
                                  ...(isSelected ? S.slotBtnSelected : {}),
                                  ...(isBooked ? S.slotBtnBooked : {}),
                                }}
                              >
                                {isBooked ? (
                                  <><i className="fas fa-ban" style={{ marginRight: "5px" }}></i>{slot.time}</>
                                ) : isSelected ? (
                                  <><i className="fas fa-check-circle" style={{ marginRight: "5px" }}></i>{slot.time}</>
                                ) : (
                                  slot.time
                                )}
                                <div style={{ fontSize: "11px", marginTop: "3px", opacity: 0.6 }}>
                                  {isBooked ? "Taken" : isSelected ? "Reserved" : slot.label}
                                </div>
                              </button>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}

                {/* Reason */}
                <div style={{ marginBottom: "8px" }}>
                  <label style={S.label}>
                    <i className="fas fa-comment-alt" style={{ marginRight: "6px", color: "#FF8C00" }}></i>
                    Reason for Rescheduling
                    <span style={{ color: "#e53e3e", marginLeft: "4px" }}>*</span>
                  </label>
                  <textarea
                    rows={4}
                    style={{
                      ...S.input,
                      resize: "vertical",
                      minHeight: "100px",
                      lineHeight: 1.6,
                      ...(reasonErr ? S.inputError : {}),
                    }}
                    placeholder="e.g. Personal emergency, medical appointment, transit issue... (min 10 characters)"
                    value={reason}
                    onChange={(e) => { setReasonErr(""); setReason(e.target.value); }}
                    maxLength={500}
                  />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "5px" }}>
                    {reasonErr
                      ? <p style={{ ...S.fieldError, margin: 0 }}>{reasonErr}</p>
                      : <span></span>
                    }
                    <span style={{ fontSize: "11px", color: "#999999" }}>{reason.length}/500</span>
                  </div>
                </div>
              </div>

              {/* ── Confirm Button Card ───────────────────────────────── */}
              <div className="reschedule-card" style={S.card}>
                <div style={S.stepHeader}>
                  <div style={S.stepBadge}>3</div>
                  <div>
                    <p style={S.stepTitle}>Confirm New Schedule</p>
                    <p style={S.stepDesc}>Review and submit. This action cannot be undone.</p>
                  </div>
                </div>

                {/* Preview of new schedule */}
                {newDate && selectedSlot && (
                  <div style={{ background: "#fff8f0", border: "1px solid #ffd9b2", borderRadius: "12px", padding: "16px 20px", marginBottom: "20px" }}>
                    <p style={{ margin: "0 0 6px", fontSize: "11px", fontWeight: "700", color: "#ff8c00", textTransform: "uppercase", letterSpacing: "0.8px" }}>
                      New Appointment Preview
                    </p>
                    <p style={{ margin: "0 0 4px", color: "#222222", fontSize: "14px", fontWeight: "600" }}>
                      <i className="fas fa-calendar-check" style={{ marginRight: "8px", color: "#ff8c00" }}></i>
                      {formatToAppDateString(newDate)}
                    </p>
                    <p style={{ margin: 0, color: "#555555", fontSize: "13px" }}>
                      <i className="fas fa-clock" style={{ marginRight: "8px", color: "#ff8c00" }}></i>
                      {selectedSlot}
                    </p>
                  </div>
                )}

                <button
                  style={{
                    ...S.btn,
                    ...((isSubmitting || !selectedSlot || !newDate || !reason.trim()) ? S.btnDisabled : {}),
                  }}
                  onClick={handleSubmit}
                  disabled={isSubmitting || !selectedSlot || !newDate || !reason.trim()}
                >
                  {isSubmitting
                    ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>Saving…</>
                    : <><i className="fas fa-calendar-check" style={{ marginRight: "8px" }}></i>Confirm New Schedule</>
                  }
                </button>

                <button
                  style={{ ...S.btn, ...S.btnSecondary, marginTop: "10px", color: "#555555" }}
                  onClick={handleReset}
                  disabled={isSubmitting}
                >
                  <i className="fas fa-arrow-left" style={{ marginRight: "8px" }}></i>
                  Start Over
                </button>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════════════════════
              STEP 3 — Success
          ════════════════════════════════════════════════════════════ */}
          {step === 3 && (
            <div className="reschedule-card" style={{ ...S.card, ...S.successCard }}>
              <span style={S.successIcon}>🎉</span>
              <h2 style={S.successTitle}>Interview Rescheduled!</h2>
              <p style={S.successMsg}>
                Your interview has been successfully rescheduled to{" "}
                <strong style={{ color: "#ff8c00" }}>{formatToAppDateString(newDate)}</strong>{" "}
                at <strong style={{ color: "#ff8c00" }}>{selectedSlot}</strong>.
                <br /><br />
                Our team will be in touch to confirm. If you have any questions, please contact us directly.
              </p>

              <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  style={{ ...S.btn, width: "auto", padding: "12px 28px" }}
                  onClick={handleReset}
                >
                  <i className="fas fa-redo" style={{ marginRight: "8px" }}></i>
                  Reschedule Another
                </button>
                <a
                  href="/Career"
                  style={{ ...S.btn, width: "auto", padding: "12px 28px", textDecoration: "none", display: "inline-flex", alignItems: "center", ...S.btnSecondary, color: "#555555" }}
                >
                  <i className="fas fa-briefcase" style={{ marginRight: "8px" }}></i>
                  View Careers
                </a>
              </div>
            </div>
          )}

        </div>
      </div>

      <Footer />
    </>
  );
};

export default RescheduleInterview;
